/**
 * The bytes behind a text or Markdown preview, decoded as UTF-8.
 *
 * The signed storage URL is fetched from the browser rather than paged through a Convex action:
 * the file is already reachable at that URL — the footer's Download is the same one — so a round
 * trip through the backend would only re-serve bytes the browser can take directly. A fetch that
 * does not land, for any reason at all, is `failed`, and the body says so beside the Download
 * that still works.
 *
 * The cap is applied twice on purpose. A row that carries a byte count is refused before
 * anything is requested, which is the only refusal that costs nothing; a row that carries none
 * is refused on the blob it landed, which at least stops a 40 MB log from being decoded and laid
 * out.
 */

import { useEffect, useState } from "react";
import {
  isTextPreviewOverCap,
  TEXT_PREVIEW_MAX_BYTES,
  type TextPreviewState,
} from "../lib/document-preview-view";

export type UseTextPreviewOptions = {
  /** The signed URL the bytes are at. */
  url: string;
  /** What the row says the file weighs, or `null` where it says nothing. */
  sizeBytes: number | null;
  /** False while the dialog showing this body is closed, or is showing another body. */
  enabled: boolean;
};

export function useTextPreview({
  url,
  sizeBytes,
  enabled,
}: UseTextPreviewOptions): TextPreviewState {
  const [state, setState] = useState<TextPreviewState>({ kind: "loading" });

  useEffect(() => {
    if (!enabled) return;
    if (isTextPreviewOverCap(sizeBytes)) {
      setState({ kind: "over_cap" });
      return;
    }

    // A new URL, or an unmount, abandons whatever the last one was doing: the request is aborted
    // and its resolution — which can still arrive — writes nothing.
    const controller = new AbortController();
    let current = true;
    setState({ kind: "loading" });

    void (async () => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`storage answered ${response.status}`);
        const blob = await response.blob();
        if (blob.size > TEXT_PREVIEW_MAX_BYTES) {
          if (current) setState({ kind: "over_cap" });
          return;
        }
        const text = await blob.text();
        if (current) setState({ kind: "ready", text });
      } catch {
        if (current) setState({ kind: "failed" });
      }
    })();

    return () => {
      current = false;
      controller.abort();
    };
  }, [enabled, sizeBytes, url]);

  return state;
}
